/**
 * FikoSTLDisaAktar — sahnedeki bir objeyi ya da bir GLB blob'unu STL'e cevirir.
 *
 * NOT: STL formati renk/doku tasimaz; yalnizca ucgen geometrisi disari aktarilir.
 * Renkli sonuc isteniyorsa GLB kullanilmalidir.
 *
 * Bu dosya bilerek "yaprak" modul olarak tutulmustur: hicbir FikoCizim* dosyasini
 * import etmez, boylece FikoCizimGLB/FikoCizimSTL buradan import edince dongu olusmaz.
 */
import * as THREE from 'three';
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

/** "ComfyUI_00004.glb" -> "ComfyUI_00004.stl" */
export function fikoStlAdi(ad: string): string {
  const temiz = (ad || 'model').replace(/\.[^.\\/]+$/, '');
  return (temiz || 'model') + '.stl';
}

/** Blob'u tarayicidan indirir. */
export function fikoDosyaIndir(blob: Blob, ad: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = ad;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 15000);
}

/** Objede STL'e girecek mesh var mi? */
export function fikoMeshSayisi(kok: THREE.Object3D): number {
  let n = 0;
  kok.traverse((e: any) => { if (e?.isMesh && e.visible !== false) n++; });
  return n;
}

/**
 * Modeli sahnenin birim sistemine (mm) olceklendirir, merkezler ve tabana oturtur.
 * (Hem sahneye ekleme hem STL disa aktarma ayni sonucu versin diye tek yerde.)
 */
export function fikoOlcekleVeOturt(kok: THREE.Object3D, hedefBoyutMm: number): void {
  const kutu = new THREE.Box3().setFromObject(kok);
  if (kutu.isEmpty() || !isFinite(kutu.min.x)) return;

  const boyut = kutu.getSize(new THREE.Vector3());
  const enBuyuk = Math.max(boyut.x, boyut.y, boyut.z);
  if (enBuyuk > 0 && hedefBoyutMm > 0) {
    kok.scale.multiplyScalar(hedefBoyutMm / enBuyuk);
  }
  kok.updateMatrixWorld(true);

  const k2 = new THREE.Box3().setFromObject(kok);
  const merkez = k2.getCenter(new THREE.Vector3());
  kok.position.x -= merkez.x;
  kok.position.y -= merkez.y;
  kok.position.z -= k2.min.z;          // tabana otursun
  kok.updateMatrixWorld(true);
}

/**
 * Sahnedeki bir objeyi (FikoCizim, Grup, Mesh...) STL blob'una cevirir.
 * Dunya matrisi kullanilir; yani ekranda gordugun olcek/konum STL'e yansir.
 * LineSegments (kenar cizgileri) STLExporter tarafindan zaten atlanir.
 */
export function fikoObjeyiStlBlob(kok: THREE.Object3D, ikili = true): Blob {
  if (!kok) throw new Error('STL icin obje yok.');
  kok.updateMatrixWorld(true);
  if (fikoMeshSayisi(kok) === 0) {
    throw new Error('Bu ogede STL\'e aktarilacak yuzey (mesh) bulunamadi.');
  }
  const sonuc: any = new STLExporter().parse(kok as any, { binary: ikili } as any);
  // ikili modda DataView/ArrayBuffer, metin modda string doner — Blob ikisini de alir.
  return new Blob([sonuc], { type: 'model/stl' });
}

/** Objeyi dogrudan .stl olarak indirir. */
export function fikoObjeyiStlIndir(kok: THREE.Object3D, ad?: string): void {
  const blob = fikoObjeyiStlBlob(kok, true);
  fikoDosyaIndir(blob, fikoStlAdi(ad || (kok as any)?.name || 'model'));
}

/**
 * GLB/GLTF blob'unu STL blob'una cevirir (renk/doku dusurulur).
 * hedefBoyutMm > 0 verilirse model once o boyuta olceklenir, merkezlenir,
 * tabana oturtulur — yani sahneye eklenenle birebir ayni STL cikar.
 */
export async function fikoGlbBlobundanStl(glb: Blob, hedefBoyutMm = 0): Promise<Blob> {
  const veri = await glb.arrayBuffer();
  const gltf: any = await new Promise((cozum, hata) => {
    new GLTFLoader().parse(veri, '', cozum, hata);
  });
  const kok: THREE.Object3D = gltf?.scene;
  if (!kok) throw new Error('GLB cozumlenemedi (sahne yok).');
  if (hedefBoyutMm > 0) fikoOlcekleVeOturt(kok, hedefBoyutMm);
  return fikoObjeyiStlBlob(kok, true);
}
